import { useEffect, useMemo, useState } from 'react';
import { api, ApiError } from '../../api/index.js';
import { useToast } from '../ui/toast.jsx';

/**
 * Settings → Nodes.
 *
 * First card: editable tier list. Replaces the hardcoded
 * (HQ/C4ISR/C2/C1) constant with an operator-managed setting. Each row:
 *
 *   value        — short uppercase code stored in nodes.tier
 *                  ([A-Z][A-Z0-9_]{0,15}). Editable for new rows only —
 *                  changing the code of an existing tier in use would
 *                  silently orphan node rows.
 *   label        — human-readable string shown in selects + badges
 *   defaultSidc  — APP-6D 20-digit code pre-filled when an admin creates
 *                  a node with this tier. Optional; blank = no default.
 *
 * Server-side guards:
 *   • cannot remove a tier currently used by any node row
 *   • duplicate codes rejected
 *   • value/label/sidc patterns enforced
 *
 * Future settings tied to nodes (default URL prefix, retention windows,
 * heartbeat limits, etc.) will accumulate as additional cards on this
 * tab.
 */

const TIER_VALUE_RE = /^[A-Z][A-Z0-9_]{0,15}$/;
const SIDC_RE = /^\d{20}$/;
const MAX_TIERS = 12;

function normalize(list) {
  return Array.isArray(list)
    ? list.map((t) => ({
        value: String(t.value || '').trim(),
        label: String(t.label || ''),
        defaultSidc: t.defaultSidc ? String(t.defaultSidc) : '',
      }))
    : [];
}

function eqList(a, b) {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i].value !== b[i].value) return false;
    if (a[i].label !== b[i].label) return false;
    if ((a[i].defaultSidc || '') !== (b[i].defaultSidc || '')) return false;
  }
  return true;
}

export function NodesTab({ onSettingsChanged }) {
  const toast = useToast();
  const [original, setOriginal] = useState(null);   // last-saved version (immutable until reload)
  const [tiers, setTiers] = useState([]);
  const [defaultPersonHomeOnly,  setDefaultPersonHomeOnly]  = useState(false);
  const [defaultChannelHomeOnly, setDefaultChannelHomeOnly] = useState(false);
  const [origDefaults, setOrigDefaults] = useState({ person: false, channel: false });
  const [loadError, setLoadError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.settings.system()
      .then((res) => {
        if (cancelled) return;
        const list = normalize(res.settings.node_tiers);
        setOriginal(list);
        setTiers(list);
        const p = res.settings.default_person_home_node_only === 'true';
        const c = res.settings.default_channel_home_node_only === 'true';
        setDefaultPersonHomeOnly(p);
        setDefaultChannelHomeOnly(c);
        setOrigDefaults({ person: p, channel: c });
      })
      .catch((err) => {
        if (!cancelled) {
          setLoadError(
            err instanceof ApiError ? err.payload?.error || err.message : 'Failed to load settings.',
          );
        }
      });
    return () => { cancelled = true; };
  }, []);

  // Per-row error map (for inline highlighting). Empty = clean.
  const errors = useMemo(() => {
    const e = {};
    const seen = new Map();           // value → first-seen row index
    tiers.forEach((t, idx) => {
      const v = (t.value || '').trim();
      if (!v) {
        e[idx] = { ...(e[idx] || {}), value: 'Code required.' };
      } else if (!TIER_VALUE_RE.test(v.toUpperCase())) {
        e[idx] = { ...(e[idx] || {}), value: '1–16 chars; A–Z, 0–9, _' };
      } else if (seen.has(v.toUpperCase())) {
        e[idx] = { ...(e[idx] || {}), value: 'Duplicate code.' };
        const first = seen.get(v.toUpperCase());
        e[first] = { ...(e[first] || {}), value: 'Duplicate code.' };
      } else {
        seen.set(v.toUpperCase(), idx);
      }
      const label = (t.label || '').trim();
      if (!label) {
        e[idx] = { ...(e[idx] || {}), label: 'Label required.' };
      } else if (label.length > 128) {
        e[idx] = { ...(e[idx] || {}), label: 'Max 128 chars.' };
      }
      const sidc = (t.defaultSidc || '').trim();
      if (sidc && !SIDC_RE.test(sidc)) {
        e[idx] = { ...(e[idx] || {}), defaultSidc: 'Must be 20 digits.' };
      }
    });
    return e;
  }, [tiers]);

  const dirty = useMemo(() => {
    if (!original) return false;
    return !eqList(normalize(tiers), original);
  }, [original, tiers]);

  const canSave = dirty
    && Object.keys(errors).length === 0
    && tiers.length >= 1
    && tiers.length <= MAX_TIERS
    && !busy;

  function update(idx, field, value) {
    setTiers((cur) => {
      const next = cur.slice();
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  }

  function addRow() {
    setTiers((cur) => [...cur, { value: '', label: '', defaultSidc: '' }]);
  }

  function removeRow(idx) {
    setTiers((cur) => cur.filter((_, i) => i !== idx));
  }

  function moveRow(idx, dir) {
    setTiers((cur) => {
      const next = cur.slice();
      const j = idx + dir;
      if (j < 0 || j >= next.length) return cur;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  }

  function reset() {
    if (!original) return;
    setTiers(original);
  }

  async function save() {
    if (!canSave) return;
    setBusy(true);
    try {
      const cleaned = tiers.map((t) => ({
        value: t.value.trim().toUpperCase(),
        label: t.label.trim(),
        defaultSidc: t.defaultSidc?.trim() ? t.defaultSidc.trim() : null,
      }));
      const res = await api.settings.updateSystem({ node_tiers: cleaned });
      const next = normalize(res.settings.node_tiers);
      setOriginal(next);
      setTiers(next);
      onSettingsChanged?.();
      toast.success('Tier list saved.');
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.payload?.error || err.message : 'Save failed.',
      );
    } finally {
      setBusy(false);
    }
  }

  // Roaming defaults — second card, separate save cycle so an in-progress
  // tier edit doesn't gate toggling a default.
  const defaultsDirty =
    defaultPersonHomeOnly  !== origDefaults.person  ||
    defaultChannelHomeOnly !== origDefaults.channel;

  async function saveDefaults() {
    if (!defaultsDirty || busy) return;
    setBusy(true);
    try {
      const res = await api.settings.updateSystem({
        default_person_home_node_only:  defaultPersonHomeOnly  ? 'true' : 'false',
        default_channel_home_node_only: defaultChannelHomeOnly ? 'true' : 'false',
      });
      const p = res.settings.default_person_home_node_only === 'true';
      const c = res.settings.default_channel_home_node_only === 'true';
      setDefaultPersonHomeOnly(p);
      setDefaultChannelHomeOnly(c);
      setOrigDefaults({ person: p, channel: c });
      onSettingsChanged?.();
      toast.success('Roaming defaults saved.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.payload?.error || err.message : 'Save failed.');
    } finally {
      setBusy(false);
    }
  }

  function resetDefaults() {
    setDefaultPersonHomeOnly(origDefaults.person);
    setDefaultChannelHomeOnly(origDefaults.channel);
  }

  if (loadError) {
    return (
      <div role="alert" className="card border-rose-200 bg-rose-soft px-5 py-4 text-sm text-rose-700">
        <i className="fa-solid fa-circle-exclamation mr-2" aria-hidden="true" />
        {loadError}
      </div>
    );
  }
  if (!original) {
    return (
      <div className="card px-6 py-12 text-center text-mist">
        <span className="loader-spinner mx-auto mb-3 text-accent" aria-hidden="true" />
        Loading…
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="card">
        <header className="flex items-center justify-between border-b border-line px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-accent-soft text-accent">
              <i className="fa-solid fa-layer-group" aria-hidden="true" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-ink">Node tiers</h2>
              <p className="text-sm text-mist">
                Network-topology classes shown in the Tier select on the Nodes page.
                Removing a tier in use is rejected — reassign affected nodes first.
              </p>
            </div>
          </div>
          <button
            type="button"
            className="btn-secondary text-xs"
            onClick={addRow}
            disabled={busy || tiers.length >= MAX_TIERS}
            title={tiers.length >= MAX_TIERS ? `Cap is ${MAX_TIERS}.` : 'Add a tier'}
          >
            <i className="fa-solid fa-plus" aria-hidden="true" /> Add tier
          </button>
        </header>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-line bg-canvas text-[11px] uppercase tracking-wider text-mist">
              <tr>
                <th className="px-3 py-2 text-left font-semibold w-[1%]">#</th>
                <th className="px-3 py-2 text-left font-semibold w-[14%]">Code</th>
                <th className="px-3 py-2 text-left font-semibold">Label</th>
                <th className="px-3 py-2 text-left font-semibold w-[24%]">Default SIDC-20</th>
                <th className="px-3 py-2 text-right font-semibold w-[1%]">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {tiers.map((t, idx) => {
                const rowErr = errors[idx] || {};
                return (
                  <tr key={idx} className="align-top">
                    <td className="px-3 py-2 text-mist tabular-nums">{idx + 1}</td>
                    <td className="px-3 py-2">
                      <input
                        className={`field font-mono uppercase ${rowErr.value ? 'border-rose-400 focus:border-rose-500' : ''}`}
                        value={t.value}
                        onChange={(e) => update(idx, 'value', e.target.value.toUpperCase())}
                        placeholder="HQ"
                        maxLength={16}
                        disabled={busy}
                      />
                      {rowErr.value && <p className="mt-1 text-xs text-rose-600">{rowErr.value}</p>}
                    </td>
                    <td className="px-3 py-2">
                      <input
                        className={`field ${rowErr.label ? 'border-rose-400 focus:border-rose-500' : ''}`}
                        value={t.label}
                        onChange={(e) => update(idx, 'label', e.target.value)}
                        placeholder="HQ — network root / management"
                        maxLength={128}
                        disabled={busy}
                      />
                      {rowErr.label && <p className="mt-1 text-xs text-rose-600">{rowErr.label}</p>}
                    </td>
                    <td className="px-3 py-2">
                      <input
                        className={`field font-mono ${rowErr.defaultSidc ? 'border-rose-400 focus:border-rose-500' : ''}`}
                        value={t.defaultSidc}
                        onChange={(e) => update(idx, 'defaultSidc', e.target.value.replace(/\D/g, '').slice(0, 20))}
                        placeholder="10031002221100000000"
                        disabled={busy}
                      />
                      {rowErr.defaultSidc && <p className="mt-1 text-xs text-rose-600">{rowErr.defaultSidc}</p>}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          className="rounded-lg p-1.5 text-mist hover:bg-canvas hover:text-ink disabled:opacity-30"
                          onClick={() => moveRow(idx, -1)}
                          disabled={busy || idx === 0}
                          title="Move up"
                        >
                          <i className="fa-solid fa-arrow-up" aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          className="rounded-lg p-1.5 text-mist hover:bg-canvas hover:text-ink disabled:opacity-30"
                          onClick={() => moveRow(idx, 1)}
                          disabled={busy || idx === tiers.length - 1}
                          title="Move down"
                        >
                          <i className="fa-solid fa-arrow-down" aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          className="rounded-lg p-1.5 text-rose-600 hover:bg-rose-soft disabled:opacity-30"
                          onClick={() => removeRow(idx)}
                          disabled={busy || tiers.length <= 1}
                          title={tiers.length <= 1 ? 'At least one tier is required.' : 'Remove tier'}
                        >
                          <i className="fa-solid fa-trash" aria-hidden="true" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {tiers.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-mist">
                    No tiers — click <strong>Add tier</strong> to get started.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <footer className="flex items-center justify-between border-t border-line bg-canvas px-6 py-4">
          <p className="text-xs text-mist">
            {tiers.length}/{MAX_TIERS} tiers.
            {dirty && <span className="ml-2 font-medium text-warning">Unsaved changes.</span>}
          </p>
          <div className="flex items-center gap-2">
            <button type="button" className="btn-secondary" onClick={reset} disabled={!dirty || busy}>
              Reset
            </button>
            <button type="button" className="btn-primary" onClick={save} disabled={!canSave}>
              {busy ? (
                <>
                  <span className="loader-spinner" aria-hidden="true" /> Saving…
                </>
              ) : (
                <>
                  <i className="fa-solid fa-floppy-disk" aria-hidden="true" /> Save changes
                </>
              )}
            </button>
          </div>
        </footer>
      </div>

      {/* ─── Roaming defaults card ──────────────────────────────────── */}
      <div className="card">
        <header className="flex items-center justify-between border-b border-line px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-accent-soft text-accent">
              <i className="fa-solid fa-tower-broadcast" aria-hidden="true" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-ink">Roaming defaults</h2>
              <p className="text-sm text-mist">
                Pre-selected value of the <em>Home node only</em> checkbox when an admin creates a new person
                or OSS channel. Per-row override stays available in each form. Default is <strong>off</strong>
                (mesh-wide); flip to lock down a security-restrictive deployment.
              </p>
            </div>
          </div>
        </header>

        <div className="grid grid-cols-1 gap-4 px-6 py-6 sm:grid-cols-2">
          <label className="flex items-start gap-3 rounded-2xl border border-line bg-canvas px-4 py-3 cursor-pointer">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4 rounded border-line accent-warning"
              checked={defaultPersonHomeOnly}
              onChange={(e) => setDefaultPersonHomeOnly(e.target.checked)}
              disabled={busy}
            />
            <div>
              <div className="flex items-center gap-2 text-sm font-medium text-ink">
                <i className="fa-solid fa-user-lock text-warning text-[11px]" aria-hidden="true" />
                Default for new persons
              </div>
              <div className="text-xs text-mist">
                When a new person is created without specifying <code className="font-mono text-[11px]">homeNodeOnly</code>,
                this value is used. Silently degrades to <em>off</em> if the unit chain has no home anchored.
              </div>
            </div>
          </label>

          <label className="flex items-start gap-3 rounded-2xl border border-line bg-canvas px-4 py-3 cursor-pointer">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4 rounded border-line accent-warning"
              checked={defaultChannelHomeOnly}
              onChange={(e) => setDefaultChannelHomeOnly(e.target.checked)}
              disabled={busy}
            />
            <div>
              <div className="flex items-center gap-2 text-sm font-medium text-ink">
                <i className="fa-solid fa-comments text-warning text-[11px]" aria-hidden="true" />
                Default for new OSS channels
              </div>
              <div className="text-xs text-mist">
                Applied to new OSS channels only. Custom (cross-unit) channels are mesh-wide by design and
                ignore this setting.
              </div>
            </div>
          </label>
        </div>

        <footer className="flex items-center justify-end gap-2 border-t border-line bg-canvas px-6 py-4">
          <button type="button" className="btn-secondary" onClick={resetDefaults} disabled={!defaultsDirty || busy}>
            Reset
          </button>
          <button type="button" className="btn-primary" onClick={saveDefaults} disabled={!defaultsDirty || busy}>
            {busy ? (
              <><span className="loader-spinner" aria-hidden="true" /> Saving…</>
            ) : (
              <><i className="fa-solid fa-floppy-disk" aria-hidden="true" /> Save defaults</>
            )}
          </button>
        </footer>
      </div>
    </div>
  );
}
